'use client';

import { createContext, useContext, useCallback } from 'react';
import { IResearchFilterContextData } from '@/creditflowresearch/domain/vo/research/IFilterContextData';
import {
  useFilterState,
  useFilterApi,
  useFilterUrl,
  useFilter,
} from '@/creditflowresearch/application/Research/hooks';

const useFilterData = (): IResearchFilterContextData => {
  const {
    isFetching,
    items,
    initialFilters,
    appliedFilters,
    filtersPayload,
    dateFromMin,
    dateToMax,
    setIsFetching,
    setItems,
    setInitialFilters,
    setAppliedFilters,
    setFiltersPayload,
    setMinMaxDatePeriod,
    resetState,
  } = useFilterState();

  const { removeFilterParamsFromUrl, updateUrlWithParams } = useFilterUrl({ shallow: true });

  const { getResearchFilter, getResearchFilterByHash } = useFilterApi(setIsFetching, setItems);

  const {
    getResearchFiltersInitialState,
    getFiltersPayload,
    applyFilter,
    fetchFilter,
    initFilters,
    resetFilters,
  } = useFilter({
    initialFilters,
    setAppliedFilters,
    setFiltersPayload,
    setMinMaxDatePeriod,
    getResearchFilter,
    removeFilterParamsFromUrl,
    updateUrlWithParams,
  });

  const payloadChange = useCallback((payload: any) => setFiltersPayload(payload), [setFiltersPayload]);
  const initialFiltersChange = useCallback((payload: any) => setInitialFilters(payload), [setInitialFilters]);
  const appliedFiltersChange = useCallback((payload: any) => setAppliedFilters(payload), [setAppliedFilters]);

  const resetResearchFilter = useCallback(() => {
    resetState();
  }, [resetState]);

  return {
    isFetching,
    items,
    initialFilters,
    appliedFilters,
    filtersPayload,
    dateFromMin,
    dateToMax,
    applyFilter,
    fetchFilter,
    getResearchFilter,
    getResearchFilterByHash,
    payloadChange,
    initialFiltersChange,
    appliedFiltersChange,
    setMinMaxDatePeriod,
    initFilters,
    resetFilters,
    resetResearchFilter,
    getResearchFiltersInitialState,
    getFiltersPayload,
  };
};

const ResearchFilterContext = createContext<IResearchFilterContextData | null>(null);

export const useResearchFilterContext = () => {
  const context = useContext(ResearchFilterContext);
  if (!context) {
    throw new Error('useResearchFilterContext must be used within the ResearchFilterContextProvider');
  }
  return context;
};

const ResearchFilterContextProvider = ({ children }: { children: React.ReactNode }) => (
  <ResearchFilterContext.Provider value={ useFilterData() }>
    { children }
  </ResearchFilterContext.Provider>
);

export default ResearchFilterContextProvider;
